let nlp: any;
try {
    nlp = require('compromise');
} catch (error) {
    console.warn('Compromise NLP library not available, using basic text processing');
    nlp = null;
}

/**
 * Processes voice dictated text to add proper punctuation and formatting
 * @param text - The raw voice input text
 * @returns Formatted text with proper punctuation
 */
export const processPunctuation = (text: string): string => {
    if (!text || text.trim().length === 0) {
        return text;
    }

    try {
        let processedText = text;

        try {
            if (nlp) {
                const doc = nlp(text);
                processedText = doc.text();
            }
        } catch (nlpError) {
            console.warn('NLP processing fallback');
        }

        processedText = processedText
            // Periods
            .replace(/\s+(period|dot|full stop|end sentence)\s*$/gi, '.')
            .replace(/\s+(period|dot|full stop|end sentence)\s+/gi, '. ')

            // Commas
            .replace(/\s+(to\s+comma|comma|put comma|insert comma|com)\s+/gi, ', ')
            .replace(/\s+(to\s+comma|comma|put comma|insert comma|com)\s*$/gi, ',')

            // Question marks
            .replace(/\s+(question mark|ask mark|inquire mark)\s*$/gi, '?')
            .replace(/\s+(question mark|ask mark|inquire mark)\s+/gi, '? ')

            // Exclamation marks
            .replace(/\s+(exclamation mark|exclamation point|bang)\s*$/gi, '!')
            .replace(/\s+(exclamation mark|exclamation point|bang)\s+/gi, '! ')

            // Colons
            .replace(/\s+(colon|insert colon)\s*$/gi, ':')
            .replace(/\s+(colon|insert colon)\s+/gi, ': ')

            // Semicolons
            .replace(/\s+(semicolon|insert semicolon)\s*$/gi, ';')
            .replace(/\s+(semicolon|insert semicolon)\s+/gi, '; ')

            // Newlines
            .replace(/\s*(new line|newline|line break|next line|return|carriage return|press enter|hit enter|enter)\s*/gi, '\n')
            .replace(/\s*(new paragraph|start new paragraph|para break)\s*/gi, '\n\n')

            // Capitalization
            .replace(/\s+(cap|capital|capitalize)\s+(\w)/gi, (_, __, letter) => ` ${letter.toUpperCase()}`)

            // Quotes
            .replace(/\s+(open quote|start quote|begin quote)\s+/gi, ' “')
            .replace(/\s+(close quote|end quote|finish quote|quote end)\s+/gi, '” ')

            // Hyphens & Dashes
            .replace(/\s+(hyphen|dash|minus)\s+/gi, '-')
            .replace(/\s+(hyphen|dash|minus)\s*$/gi, '-')

            // Slashes
            .replace(/\s+(slash|forward slash)\s+/gi, '/')
            .replace(/\s+(slash|forward slash)\s*$/gi, '/')

            // Parentheses
            .replace(/\s+(open parentheses|left paren|start paren)\s+/gi, ' (')
            .replace(/\s+(close parentheses|right paren|end paren)\s+/gi, ') ')

            // Cleanup spacing (preserve newlines)
            .replace(/[^\S\n]+/g, ' ')
            .replace(/^[ \t]+|[ \t]+$/gm, '');

        // Capitalize first letter and sentence starts
        if (processedText.length > 0) {
            processedText = processedText.charAt(0).toUpperCase() + processedText.slice(1);
            processedText = processedText.replace(/\.\s+([a-z])/g, (_, letter) => `. ${letter.toUpperCase()}`);
        }

        return processedText;
    } catch (error) {
        console.warn('Error processing punctuation:', error);
        return text;
    }
};

/**
 * Checks if the input text contains punctuation commands
 * @param text - The voice input text
 * @returns True if the text contains punctuation commands
 */
export const containsPunctuationCommands = (text: string): boolean => {
    const punctuationCommands = [
        'period', 'dot', 'full stop', "end sentence",
        'comma', 'to comma', "comma", "put comma", "insert comma", "com",
        "enter", "press enter", "hit enter", "return", "carriage return", "new line",
        "line break", "next line",
        'question mark', "ask mark", "inquire mark",
        'exclamation mark', 'exclamation point', "bang",
        'colon', "semi colon", "insert colon",
        'semicolon', "insert semicolon",
        'new line', 'newline', 'new paragraph', "start new paragraph", "para break",
        'cap', 'capital', 'capitalize',
        "open quote", "start quote", "begin quote",
        "close quote", "end quote", "finish quote", "quote end",
        "hyphen", "dash", "minus",
        "slash", "forward slash",
        "open parentheses", "left paren", "start paren",
        "close parentheses", "right paren", "end paren"
    ];

    const lowerText = text.toLowerCase();
    return punctuationCommands.some(command => lowerText.includes(command));
};

/**
 * Processes text for medical report formatting with common medical terms
 * @param text - The voice input text
 * @returns Formatted text with medical-specific formatting
 */
export const processMedicalText = (text: string): string => {
    if (!text || text.trim().length === 0) {
        return text;
    }

    try {
        let processedText = processPunctuation(text);

        processedText = processedText
            .replace(/\b(ct|mri|pet|xray|x-ray)\b/gi, (match) => match.toUpperCase())
            .replace(/\b(mm|cm|ml|kg|mg)\b/gi, (match) => match.toLowerCase())

            // Medical measurements
            .replace(/(\d+)\s*(millimeter|millimeters)/gi, '$1 mm')
            .replace(/(\d+)\s*(centimeter|centimeters)/gi, '$1 cm')
            .replace(/(\d+)\s*(milliliter|milliliters)/gi, '$1 ml')
            .replace(/(\d+)\s*(kilogram|kilograms)/gi, '$1 kg')
            .replace(/(\d+)\s*(milligram|milligrams)/gi, '$1 mg')

            // Handle anatomical chamber references (e.g., "3 and 4 chamber")
            .replace(/(\d+)\s+and\s+(\d+)\s+chamber/gi, '$1- and $2-chamber')

            // Common medical terms formatting
            .replace(/\b(no evidence of|neg|negative)\b/gi, 'negative')
            .replace(/\b(pos|positive)\b/gi, 'positive')
            .replace(/\b(abnl|abnormal)\b/gi, 'abnormal')
            .replace(/\b(wnl|within normal limits)\b/gi, 'within normal limits')

            // Handle axis terminology
            .replace(/\b(short axis|long axis)\b/gi, (match) => {
                return match.toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
            });

        return processedText;
    } catch (error) {
        console.warn('Error processing medical text:', error);
        return text;
    }
};
